import type { FC, ReactNode } from 'react';

import { IcoMoonIcon } from '@wwe-portal/ui/components/icons/icomoon';

import { FooterContent } from './footer-content';
import { LogoMenu } from './logo-menu';
import { SeperateLine } from './seperate-line';
import { cn } from '@wwe-portal/ui/lib/lib';
import { ClickableDiv } from '../../../components/button';

interface LeftSidebarProps {
  logo: ReactNode;
  websiteName: ReactNode;
  websiteSubtitle?: ReactNode;
  appStore: ReactNode;
  googlePlay: ReactNode;
  backToGame?: ReactNode;
  menus: ReactNode;
  forumButton?: ReactNode;
  playGameContent: ReactNode;
  mobilePlayGameContent: ReactNode;
  closeSidebarButton: ReactNode;
  /** Internal app route link; injected because this package has no next/link. */
  oddsLink?: ReactNode;
  className?: string;
  reloadOTBanner: () => void;
  socialLinks: {
    key: string;
    url: string;
    icon: string;
  }[];
  id: string;
}
export const LeftSidebar: FC<LeftSidebarProps> = ({
  menus,
  logo,
  websiteName,
  websiteSubtitle,
  appStore,
  googlePlay,
  forumButton,
  backToGame,
  playGameContent,
  mobilePlayGameContent,
  className,
  reloadOTBanner,
  socialLinks,
  closeSidebarButton,
  oddsLink,
  id,
}) => {
  return (
    <div id={id} className={cn('bg-back600 overflow-auto px-5', className)}>
      <div className="-mr-5 flex items-center justify-between xl:mb-10 xl:mt-5">
        <LogoMenu
          logo={logo}
          websiteName={websiteName}
          subtitle={websiteSubtitle}
        />
        {closeSidebarButton}
      </div>
      <SeperateLine className="-mx-5 mb-5 xl:hidden" />
      {playGameContent}
      <div className="-mx-5">{menus}</div>
      <div className="mt-4 md:hidden">
        {mobilePlayGameContent}
        {backToGame}
        <SeperateLine className="mt-4" />
      </div>
      <SeperateLine />
      {forumButton}
      <SeperateLine />
      <div className="my-10 flex flex-col gap-2">
        {appStore}
        {googlePlay}
      </div>
      <SeperateLine />
      <div className="text-text600 mt-10 flex flex-col gap-4">
        <a
          className="text-text600 hover:text-green700 hover:no-underline"
          data-id="left_sidebar_terms_link"
          href="https://scopely.com/tos/"
          rel="noopener noreferrer"
          target="_blank"
        >
          Terms of Service
        </a>
        <a
          className="text-text600 hover:text-green700 hover:no-underline"
          data-id="left_sidebar_privacy_link"
          href="https://scopely.com/privacy/"
          rel="noopener noreferrer"
          target="_blank"
        >
          Privacy Policy
        </a>
        <ClickableDiv
          id="ot-sdk-link"
          className="hover:text-green700 cursor-pointer"
          onClick={reloadOTBanner}
        >
          Cookies
        </ClickableDiv>
        {oddsLink}
      </div>
      <div className="mt-6 flex justify-between gap-4">
        {socialLinks.map((item) => (
          <a
            key={item.key}
            href={item.url}
            rel="noopener noreferrer"
            target="_blank"
          >
            <IcoMoonIcon
              className="text-back200 hover:text-text700 max-h-[26px] max-w-[26px] cursor-pointer"
              iconName={item.icon}
            />
          </a>
        ))}
      </div>
      <FooterContent />
    </div>
  );
};
